import { Box, Chip, Container, Divider, Skeleton, Stack, styled, Typography, useTheme } from '@mui/material';
import { useFormik } from 'formik';
import { isArray, isEmpty, isString, pickBy, startCase } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';

// Components
import { CheckCircle, ExternalLink, XCircle } from 'react-feather';
import { useTranslation } from 'react-i18next';
import LinkToCompanyDetail from 'src/components/LinkToCompanyDetail';
import ButtonBookmark from 'src/components/common/ButtonBookmark';
import FlagCountry from 'src/components/common/FlagCountry';
import FormikForm from 'src/components/common/FormikForm';
import IconFeather from 'src/components/common/Icon';
import LoadingSkeleton from 'src/components/common/LoadingSkeleton';
import LogoCompany from 'src/components/common/LogoCompany';
import RouterLinkNoUnderline from 'src/components/common/RouterLinkNoUnderline';
import FormBookmarkNote from 'src/features/Bookmark/FormBookmarkNote';
import FormItemChooseCompany from 'src/features/CompareCompany/Common/FormItemChooseCompany';
import { TypeCompareCompany, TypeCompareSocialMedia } from 'src/features/CompareCompany/Common/config';
import PopupMedia from 'src/features/CompareCompany/PopupMedia';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { BookmarkTypes, IBookmarkCompareCreatePayload } from 'src/model/bookmark';
import { useMutateCreateBookmark, useMutateUpdateBookmarkByDataUuid } from 'src/services/mutate/useMutateBookmark';
import { useQueryCompareCompanies } from 'src/services/queries';
import { useQueryGetBookmarkByDataUuid, useQueryGetDataUuid } from 'src/services/queries/useQueryBookmark';
import { EmptyText, FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { SUCCESS_CODE } from 'src/utils/constants/statusCode';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { formatUTCTime } from 'src/utils/helpers/dayjs';
import {
  checkCountryHaveState,
  getCompanyAgeText,
  getCountryByCountryCode,
  getStringOrDefault,
  renderTextOrDefault,
} from 'src/utils/helpers/stringHelpers';
import { SectionErrorBoundary } from 'src/components/ErrorBoundary/SectionErrorBoundary';

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(7),
}));

const CompareCompany = () => {
  const theme = useTheme();
  const [, setSearchParam] = useSearchParams();
  const { companyIds } = useQueryParams();
  const [isOpenBookmarkNoteForm, setIsOpenBookmarkNoteForm] = useState(false);
  const [note, setNote] = useState<string>('');
  const [isBookmark, setIsBookmark] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<undefined | ICompareCompany>(undefined);
  const [selectedMediaType, setSelectedMediaType] = useState('contact' as TypeCompareSocialMedia);
  const [visiblePopupMedia, setVisiblePopupMedia] = useState(false as boolean);

  const navigate = useNavigate();
  const payloadCheckBookmark: IBookmarkCompareCreatePayload = useMemo(
    () => ({
      company_ids: companyIds,
    }),
    [companyIds],
  );

  useEffect(() => {
    if (companyIds && isString(companyIds)) {
      navigate(AuthRouteKeys.CompanyDetail.replace(':id', companyIds));
    }
  }, [companyIds]);

  const GetCompareCompaniesQuery = useQueryCompareCompanies(companyIds || []);
  const { data: dataUuid } = useQueryGetDataUuid(payloadCheckBookmark);
  const { data: dataBookmark } = useQueryGetBookmarkByDataUuid(BookmarkTypes.CompareCompany, dataUuid?.data || '');
  const { mutateAsync: createBookmarkMutate, isPending: isPendingCreateBookmark } = useMutateCreateBookmark();
  const { mutateAsync: updateBookmarkMutate, isPending: isPendingUpdateBookmark } = useMutateUpdateBookmarkByDataUuid();
  const dataCompareCompanies = getArrayDefault(GetCompareCompaniesQuery.data?.data).filter((item) => !isEmpty(item));
  const { t } = useTranslation();
  const formik = useFormik<TypeCompareCompany>({
    enableReinitialize: true,
    initialValues: isArray(companyIds)
      ? companyIds.filter(Boolean).reduce((result: ExpectedAny, id: string, index: number) => {
          result[`company_${index}`] = id;
          return result;
        }, {})
      : {},
    validationSchema: Yup.object().shape({}),

    onSubmit: async (values, { setSubmitting }) => {
      const companyIds = pickBy(values, (_, key) => {
        return key.startsWith('company_');
      });
      setSearchParam({
        companyIds: Object.values(companyIds),
      });
      setSubmitting(false);
    },
  });

  useEffect(() => {
    // Fetch data bookmark done
    setIsBookmark(!isEmpty(dataBookmark?.data)); // If data bookmark is empty => This company is not marked yet.
    if (isEmpty(dataBookmark?.data)) {
      setNote('');
    } else {
      setNote(dataBookmark?.data?.note || '');
    }
  }, [dataBookmark?.data?.note]);
  const handleCloseBookmarkNoteForm = () => {
    setIsOpenBookmarkNoteForm(false);
  };

  const handleOpenBookmarkNoteForm = () => {
    setIsOpenBookmarkNoteForm(true);
  };

  const handleSubmitBookmarkNoteForm = useCallback(async () => {
    const response = isBookmark
      ? await updateBookmarkMutate({
          type: BookmarkTypes.CompareCompany,
          dataUuid: dataUuid?.data || '',
          body: {
            note,
          },
        })
      : await createBookmarkMutate({
          type: BookmarkTypes.CompareCompany,
          body: { ...payloadCheckBookmark, note },
        });

    if (response?.status_code === SUCCESS_CODE.CREATE) {
      setIsBookmark(true);
    }

    handleCloseBookmarkNoteForm();
  }, [payloadCheckBookmark, note, isBookmark, dataUuid?.data]);

  const renderLabelWithCheckboxIcon = (type: TypeCompareSocialMedia, record: ICompareCompany, checked?: boolean) => {
    if (checked)
      return (
        <Typography
          sx={{ cursor: 'pointer' }}
          component={'span'}
          onClick={() => {
            setSelectedRecord(record);
            setSelectedMediaType(type);
            setVisiblePopupMedia(true);
          }}
          display={'flex'}
          alignItems={'center'}
          gap={1}
          variant="body2"
          color={'secondary.main'}
        >
          <IconFeather muiProps={{ color: 'success', fontSize: 'small' }} icon={<CheckCircle />} /> {startCase(type)}
        </Typography>
      );
    return <IconFeather muiProps={{ color: 'error', fontSize: 'small' }} icon={<XCircle />} />;
  };

  const dataCompareCompany = useMemo(() => {
    return [
      {
        label: t('compare_companies.field.label.company_name'),
        values: dataCompareCompanies.map((company) => {
          return (
            <LinkToCompanyDetail
              key={company.id}
              companyName={getStringOrDefault(company.name)}
              id={getStringOrDefault(company.id)}
            />
          );
        }),
      },
      {
        label: t('compare_companies.field.label.description'),
        values: dataCompareCompanies.map((company) => renderTextOrDefault(company.description)),
      },
      {
        label: t('compare_companies.field.label.company_number'),
        values: dataCompareCompanies.map((company) => company?.company_code || EmptyText),
      },
      {
        label: t('compare_companies.field.label.company_type'),
        values: dataCompareCompanies.map((company) => company?.company_type || EmptyText),
      },
      {
        label: t('compare_companies.field.label.jurisdiction'),
        values: dataCompareCompanies.map((company, index) => {
          const isCountryHaveState = checkCountryHaveState(company.country || '');
          const country = getCountryByCountryCode(company?.country || '');
          return (
            <Stack key={index} spacing={1} direction="row" alignItems="center">
              <FlagCountry code={company?.country || ''} width={16} />
              <Typography variant="body2" color="text.secondary">
                {isCountryHaveState
                  ? `${country && `${country.label} - `} ${company.jurisdiction}`
                  : `${country && country.label}`}
              </Typography>
            </Stack>
          );
        }),
      },
      {
        label: t('compare_companies.field.label.lei'),
        values: dataCompareCompanies.map((company) => company?.lei || EmptyText),
      },
      {
        label: t('compare_companies.field.label.date_incorporated'),
        values: dataCompareCompanies.map((company) =>
          company?.date_incorporated
            ? formatUTCTime(FORMAT_DATE_TIME.FULL_ONLY_DATE, company?.date_incorporated || '')
            : EmptyText,
        ),
      },
      {
        label: t('compare_companies.field.label.company_age'),
        values: dataCompareCompanies.map((company) => getCompanyAgeText(company?.company_age)),
      },
      {
        label: t('compare_companies.field.label.number_of_customers'),
        values: dataCompareCompanies.map((company) => renderTextOrDefault(company.total_buyers)),
      },
      {
        label: t('compare_companies.field.label.number_of_suppliers'),
        values: dataCompareCompanies.map((company) => renderTextOrDefault(company.total_suppliers)),
      },
      {
        label: t('compare_companies.field.label.registered_address'),
        values: dataCompareCompanies.map((company) => (company?.registered_address || EmptyText).toUpperCase()),
      },
      {
        label: t('compare_companies.field.label.phone'),
        values: dataCompareCompanies.map((company) =>
          !isEmpty(company?.phone_numbers) ? getArrayDefault(company?.phone_numbers).join(', ') : EmptyText,
        ),
      },
      {
        label: t('compare_companies.field.label.fax'),
        values: dataCompareCompanies.map((company) =>
          !isEmpty(company?.fax_numbers) ? getArrayDefault(company?.fax_numbers).join(', ') : EmptyText,
        ),
      },
      {
        label: t('compare_companies.field.label.email'),
        values: dataCompareCompanies.map((company) =>
          !isEmpty(company?.emails) ? getArrayDefault(company?.emails).join(', ') : EmptyText,
        ),
      },
      {
        label: t('compare_companies.field.label.website'),
        values: dataCompareCompanies.map((company) => {
          if (isEmpty(company.websites)) return EmptyText;

          return (
            <Stack key={`website-compare-company-${company.id}`} spacing={1}>
              {getArrayDefault(company.websites).map((site) => (
                <RouterLinkNoUnderline
                  key={`website-item-compare-company-${company.id}-${site}`}
                  target="_blank"
                  to={site}
                >
                  <Typography variant="body2" color={'secondary.main'}>
                    {site} <ExternalLink size={12} />
                  </Typography>
                </RouterLinkNoUnderline>
              ))}
            </Stack>
          );
        }),
      },
      {
        label: t('compare_companies.field.label.contacts'),
        values: dataCompareCompanies.map((company) =>
          renderLabelWithCheckboxIcon('contact', company, company.is_contact),
        ),
      },
      {
        label: t('compare_companies.field.label.social_media'),
        values: dataCompareCompanies.map((company) =>
          renderLabelWithCheckboxIcon('social_media', company, company.is_social_media),
        ),
      },
      {
        label: t('compare_companies.field.label.negative_media'),
        values: dataCompareCompanies.map((company) =>
          renderLabelWithCheckboxIcon('negative_media', company, company.is_negative_media),
        ),
      },
      {
        label: t('compare_companies.field.label.stock_listing'),
        values: dataCompareCompanies.map((company) =>
          renderLabelWithCheckboxIcon('stock_listing', company, company.stock_listing),
        ),
      },
      {
        label: t('compare_companies.field.label.trade_marks'),
        values: dataCompareCompanies.map((company) => EmptyText),
      },
      {
        label: t('compare_companies.field.label.date_struck_off'),
        values: dataCompareCompanies.map((company) =>
          formatUTCTime(FORMAT_DATE_TIME.FULL_ONLY_DATE, company?.date_struck_off || ''),
        ),
      },
      {
        label: t('compare_companies.field.label.alerts'),
        values: dataCompareCompanies.map((company, index) =>
          company?.is_sanctioned ? (
            <Chip key={index} label="Sanctioned" color="warning" size="small" />
          ) : (
            <Chip key={index} label="Not Found" color="default" size="small" />
          ),
        ),
      },
      {
        label: (
          <Typography variant={'body2'} color={'text.disabled'}>
            {t('compare_companies.field.label.buyer_global_rank')}
          </Typography>
        ),
        values: dataCompareCompanies.map((_, index) => (
          <Typography key={index} variant="body2" color="text.disabled">
            {EmptyText}
          </Typography>
        )),
      },
      {
        label: (
          <Typography variant={'body2'} color={'text.disabled'}>
            {t('compare_companies.field.label.buyer_country_rank')}
          </Typography>
        ),
        values: dataCompareCompanies.map((_, index) => (
          <Typography key={index} variant="body2" color="text.disabled">
            {EmptyText}
          </Typography>
        )),
      },
      {
        label: (
          <Typography variant={'body2'} color={'text.disabled'}>
            {t('compare_companies.field.label.seller_global_rank')}
          </Typography>
        ),
        values: dataCompareCompanies.map((_, index) => (
          <Typography key={index} variant="body2" color="text.disabled">
            {EmptyText}
          </Typography>
        )),
      },
      {
        label: (
          <Typography variant={'body2'} color={'text.disabled'}>
            {t('compare_companies.field.label.seller_country_rank')}
          </Typography>
        ),
        values: dataCompareCompanies.map((_, index) => (
          <Typography key={index} variant="body2" color="text.disabled">
            {EmptyText}
          </Typography>
        )),
      },
      {
        label: (
          <Typography variant={'body2'} color={'text.disabled'}>
            {t('compare_companies.field.label.number_of_foreign_customers_12_months')}
          </Typography>
        ),
        values: dataCompareCompanies.map((_, index) => (
          <Typography key={index} variant="body2" color="text.disabled">
            {EmptyText}
          </Typography>
        )),
      },
      {
        label: (
          <Typography variant={'body2'} color={'text.disabled'}>
            {t('compare_companies.field.label.number_of_foreign_customers_all_time')}
          </Typography>
        ),
        values: dataCompareCompanies.map((_, index) => (
          <Typography key={index} variant="body2" color="text.disabled">
            {EmptyText}
          </Typography>
        )),
      },
      {
        label: (
          <Typography variant={'body2'} color={'text.disabled'}>
            {t('compare_companies.field.label.number_of_foreign_suppliers_12_months')}
          </Typography>
        ),
        values: dataCompareCompanies.map((_, index) => (
          <Typography key={index} variant="body2" color="text.disabled">
            {EmptyText}
          </Typography>
        )),
      },
      {
        label: (
          <Typography variant={'body2'} color={'text.disabled'}>
            {t('compare_companies.field.label.number_of_foreign_suppliers_all_time')}
          </Typography>
        ),
        values: dataCompareCompanies.map((_, index) => (
          <Typography key={index} variant="body2" color="text.disabled">
            {EmptyText}
          </Typography>
        )),
      },
    ];
  }, [dataCompareCompanies]);

  return (
    <StyledContainer maxWidth={'xl'}>
      <SectionErrorBoundary resetKey="CompareCompanyPopupMedia" sectionName="CompareCompanyPopupMedia">
        <PopupMedia
          selectedRecord={selectedRecord}
          selectedMediaType={selectedMediaType}
          handleClose={() => setVisiblePopupMedia(false)}
          open={visiblePopupMedia}
        />
      </SectionErrorBoundary>
      <Box
        padding={3}
        sx={{
          background: theme.palette.background.default,
          borderRadius: theme.shape.borderRadius,
        }}
      >
        <Stack spacing={2}>
          <Stack direction={'row'} flexWrap="wrap" justifyContent={'space-between'}>
            <Typography variant="h4">{t('compare_companies.header.title')}</Typography>
            <ButtonBookmark
              isMarked={isBookmark}
              buttonProps={{
                onClick: () => handleOpenBookmarkNoteForm(),
              }}
            />
          </Stack>
          <Typography variant="body1" color="text.secondary">
            {t('compare_companies.header.description')}
          </Typography>
          <SectionErrorBoundary resetKey="CompareCompanyResults" sectionName="CompareCompanyResults">
            {GetCompareCompaniesQuery.isFetching ? (
              <SkeletonContent />
            ) : (
              <Box display={dataCompareCompanies.length > 1 ? 'block' : 'none'}>
                <FormikForm formik={formik}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    divider={<Divider orientation="vertical" flexItem />}
                    sx={{
                      background: 'linear-gradient(180deg, #7551FF 0%, #3311DB 100%)',
                      boxShadow: '0px 0px 50px 0px rgba(112, 114, 176, 0.08)',
                      borderTopLeftRadius: '8px',
                      borderTopRightRadius: '8px',
                    }}
                  >
                    <Box display="flex" flexBasis={'25%'} />
                    {dataCompareCompanies.map((company, index) => {
                      return (
                        <Box
                          flexBasis={`${(100 - 25) / dataCompareCompanies.length}%`}
                          key={company.name}
                          display="flex"
                          p={4}
                          flexDirection="column"
                          gap={3}
                          justifyContent={'space-between'}
                        >
                          <Box>
                            <LogoCompany logoURL={company?.logo_url || ''} width={64} height={64} />
                            <Typography marginTop={2} variant="h6" color="common.white">
                              {company?.name || EmptyText}
                            </Typography>
                          </Box>
                          <Box>
                            <FormItemChooseCompany
                              defaultValue={company}
                              disabledList={dataCompareCompanies.map((company) => company.id as string)}
                              placeholder={t('compare_companies.search_form.title')}
                              id={`autocomplete_company_compare_${index}`}
                              sx={{
                                '& .MuiAutocomplete-input': {
                                  color: theme.palette.common.white,
                                },
                                '& .MuiAutocomplete-clearIndicator': {
                                  color: theme.palette.common.white,
                                },
                                '& .MuiOutlinedInput-root': {
                                  borderColor: `${theme.palette.common.white} !important`,
                                  '&.Mui-focused fieldset': {
                                    borderColor: theme.palette.common.white,
                                    borderWidth: 1,
                                  },
                                  '&:hover fieldset': {
                                    borderColor: `${theme.palette.common.white} !important`,
                                  },
                                },
                                '& .MuiInput-underline:before': {
                                  color: theme.palette.common.white,
                                },
                                '& .MuiInputBase-input::placeholder': {
                                  color: theme.palette.common.white,
                                },
                              }}
                              onChange={(value) => {
                                if (!value) return;
                                formik.setFieldValue(`company_${index}`, value);
                                formik.handleSubmit?.();
                              }}
                            />
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </FormikForm>
                {dataCompareCompany.map((data, index) => (
                  <Stack key={`data-compare-company-${index}`} direction="row">
                    <Box
                      flexBasis={`25%`}
                      display="flex"
                      py={2}
                      px={4}
                      borderBottom={`1px dashed ${theme.palette.divider}`}
                      borderRight={`1px dashed ${theme.palette.divider}`}
                    >
                      {typeof data.label === 'string' ? (
                        <Typography variant="subtitle2">{data.label}</Typography>
                      ) : (
                        data.label
                      )}
                    </Box>
                    {data.values.map((value, index) => {
                      const isLastElement = index === data.values.length - 1;
                      return (
                        <Box
                          flexBasis={`${(100 - 25) / dataCompareCompanies.length}%`}
                          display="flex"
                          py={2}
                          px={4}
                          key={index}
                          borderRight={!isLastElement ? `1px dashed ${theme.palette.divider}` : 'none'}
                          borderBottom={`1px dashed ${theme.palette.divider}`}
                        >
                          {typeof value === 'string' || typeof value === 'number' ? (
                            <Typography sx={{ wordBreak: 'break-all' }} variant="body2" color="text.secondary">
                              {value}
                            </Typography>
                          ) : (
                            value
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                ))}
              </Box>
            )}
          </SectionErrorBoundary>
        </Stack>
      </Box>
      <FormBookmarkNote
        open={isOpenBookmarkNoteForm}
        handleClose={handleCloseBookmarkNoteForm}
        handleSubmit={() => handleSubmitBookmarkNoteForm()}
        value={note}
        onChange={(value) => setNote(value)}
        isLoading={isPendingCreateBookmark || isPendingUpdateBookmark}
      />
    </StyledContainer>
  );
};

export default CompareCompany;

const SkeletonContent = () => {
  return (
    <Stack spacing={1}>
      <Skeleton variant="rectangular" animation="wave" height={240} />
      <LoadingSkeleton height={40} count={20} />
    </Stack>
  );
};
